import { useCallback, useMemo } from 'react';
import { create } from 'zustand';

// Los avisos viven en un almacén global y los pinta <ToastHost /> (montado una vez en App).
// Antes cada pantalla guardaba sus avisos en un estado local que ninguna pintaba: nunca se veían.
const useToastStore = create((set) => ({
  toasts: [],
  push: (t) => set(s => ({ toasts: [...s.toasts.slice(-4), t] })),
  remove: (id) => set(s => ({ toasts: s.toasts.filter(t => t.id !== id) })),
  setAll: (fn) => set(s => ({ toasts: typeof fn === 'function' ? fn(s.toasts) : fn })),
}));

let siguienteId = 1;

// Misma firma que antes: { toasts, addToast, setToasts }
export function useToast() {
  const toasts = useToastStore(s => s.toasts);
  const push = useToastStore(s => s.push);
  const remove = useToastStore(s => s.remove);
  const setToasts = useToastStore(s => s.setAll);

  // Estable entre renders: las pantallas lo ponen en dependencias de useEffect/useCallback y,
  // si cambiara en cada render, re-disparaban sus peticiones en bucle (/perfil, /espacios, /inversiones).
  const addToast = useCallback((message, type = 'info', duration = 3500) => {
    const id = siguienteId++;
    push({ id, message, type });
    if (duration > 0) setTimeout(() => remove(id), duration);
    return id;
  }, [push, remove]);

  return useMemo(() => ({ toasts, addToast, setToasts }), [toasts, addToast, setToasts]);
}

const ESTILOS = {
  success: 'bg-green-600',
  error: 'bg-red-600',
  warning: 'bg-amber-600',
  info: 'bg-blue-600',
};

export function ToastHost() {
  const toasts = useToastStore(s => s.toasts);
  const remove = useToastStore(s => s.remove);
  return (
    <div className="fixed top-16 right-4 left-4 sm:left-auto z-[2000] space-y-2 pointer-events-none" aria-live="polite" role="status">
      {toasts.map(toast => (
        <div
          key={toast.id}
          className={`pointer-events-auto sm:max-w-sm ml-auto px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium flex items-center justify-between gap-4 animate-slide-down ${ESTILOS[toast.type] || ESTILOS.info}`}
        >
          <span>{toast.message}</span>
          <button onClick={() => remove(toast.id)} className="-my-2 -mr-2 shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-white hover:text-white/80 font-bold" aria-label="Cerrar aviso">✕</button>
        </div>
      ))}
    </div>
  );
}

// Compatibilidad: algunas pantallas pintaban <Toast toasts={...} onRemove={...} /> por su cuenta.
// Ahora lo pinta ToastHost; este componente ya no dibuja nada para no duplicar avisos.
export function Toast() {
  return null;
}
